import React, { useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SailingIcon from '@mui/icons-material/Sailing';
import AnchorIcon from '@mui/icons-material/Anchor';
import ApprovalIcon from '@mui/icons-material/Approval';
import PublishIcon from '@mui/icons-material/Publish';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
} from '../../../../../../common/APICommon';
import { LoadingSpinner } from '../../../../../../common/Loading';
import { formatDateLong } from '../../../../../../utils/textUtils';
import { getStatusConfig } from '../../../../../../utils/enums';

import { EmployeeTransactionModal } from '../../modal/EmployeeTransactionModal';
import { useEmployeeTransaction } from "../../../../../hooks/useEmployeeTransaction";
import { DynamicFilterBar } from "../../../../filters/DynamicFilterBar";
import { FEATURES } from "../../../../../../config/features";
import { ConfirmationModal } from "../../../../../../common/Modal";
import { set } from "date-fns";

export const ProcessManagerMain = ({ user, userAccess, showGeneralMessage, filterManager }) => {

  // destructure to get the filter states and handlers
  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    applyFilters,
    resetFilters
  } = filterManager;

  // MODIFIED: requestLoading, handleAdd, handleUpdate now come from the hook
  const {
    loading,
    dropdownsLoading,
    requestLoading,
    data,
    options,
    salaryData,
    fetchData,
    handleFilter,
    handleAdd,
    handleUpdate,
    handleSubmitForApproval,
    handleSubmitForBypassApproval,
    handleMarkAsDeparted,
    handleMarkAsArrived,
  } = useEmployeeTransaction({
    user,
    userAccess,
    showGeneralMessage,
    apiFilterPayload
  });

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedData, setSelectedData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({
    title: "",
    message: "",
    action: null,
    dateTimeTitle: "",
    hasDateTime: false,
  });

  const openModal = (item = null, formMode = "add") => {
    setSelectedData(item);
    setMode(formMode);
    setShowModal(true);
  };

  const headers = [
    "ID",
    "Employee No",
    "Employee Name",
    "Transaction Code",
    "Effective Date",
    "Destination Vessel",
    "Temp Vessel",
    "Temp Title",
    "Destination Head",
    "Via Captain",
    "Status",
    "Created At",
    "Actions"
  ];
  const accessors = [
    "id",
    "employeeNumber",
    "employeeName",
    "employeeTransactionAccessCode",
    "effectiveFrom",
    "destinationVesselName",
    "tempVesselName",
    "tempPositionName",
    "destinationHeadName",
    "assignedToName", // wiil change this shit
    "status",
    "createdAt",
    "actions"
  ];

  const actions = [
    {
      name: "Edit",
      tooltip: "Edit Details",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: (item) => openModal(item, "edit"),
      visible: (row) => row.status === "Draft",
    },
    {
      name: "View",
      tooltip: "View Details",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-blue-500 hover:bg-blue-600 flex items-center",
      function: (item) => openModal(item, "view"),
      visible: true,
    },
    {
      name: "For Approval",
      tooltip: "Submit for Approval",
      icon: <ApprovalIcon fontSize="small" />,
      className: "bg-amber-500 hover:bg-amber-600 flex items-center", // Amber suggests "Warning/Action required"
      function: (item) => handleCofirmation(item),
      visible: (row) => row.status === "Draft" && userAccess?.isEmployeeTransactionProcessManager === true && !userAccess?.allowSkipApproval,
    },
    {
      name: "Finalize",
      tooltip: "Submit Transaction",
      icon: <PublishIcon fontSize="small" />,
      className: "bg-amber-500 hover:bg-amber-600 flex items-center", // Amber suggests "Warning/Action required"
      function: (item) => handleCofirmation(item),
      visible: (row) => row.status === "Draft" && userAccess?.allowSkipApproval === true, // EmployeeTransactionStatus.Draft (Backend)
    },
    {
      name: "Departed",
      tooltip: "Mark Employee as Departed",
      icon: <SailingIcon fontSize="small" />,
      className: "bg-orange-500 hover:bg-orange-600 flex items-center",
      function: (item) => handleCofirmation(item),
      visible: (row) => {
        const isVisible = row.status === "Approved" &&
          userAccess?.isEmployeeTransactionProcessManager === true &&
          userAccess?.allowDepartedButton === true &&
          row.requiredSailingButtons === true; // EmployeeTransactionStatus.OnSail (Backend)

        return isVisible;
      }
    },
    {
      name: "Arrive",
      tooltip: "Mark Employee as Arrived",
      icon: <AnchorIcon fontSize="small" />,
      className: "bg-teal-500 hover:bg-teal-600 flex items-center",
      function: (item) => handleCofirmation(item),
      visible: (row) => {
        const isVisible = row.status === "Departed" &&
          userAccess?.isEmployeeTransactionProcessManager === true &&
          userAccess?.allowArrivedButton === true &&
          row.requiredSailingButtons === true; // EmployeeTransactionStatus.Departed (Backend)

        return isVisible;
      }
    },
  ];

  const renderCustomCell = (row, accessor) => {
    if (accessor === 'status') {
      const config = getStatusConfig(row.status);
      return (
        <span className={`px-3 py-1 text-[11px] rounded-full font-bold border uppercase tracking-wide
          ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
          {row.status}
        </span>
      );
    }
    if (accessor === 'effectiveFrom' || accessor === 'effectiveTo' || accessor === 'createdAt') {
      if (!row[accessor]) return "N/A";

      return formatDateLong(row[accessor]);
    }
    return null;
  };

  const handleCofirmation = (data) => {
    const { status } = data;

    if (status === "Draft") { // Setting up to mark the request for approval
      if (userAccess?.allowSkipApproval !== true) {
        setConfirmationConfig({
          title: "Submit for Approval",
          message: "Are you sure you want to submit this transaction for approval? Once submitted, it cannot be edited.",
          action: async () => {
            await handleSubmitForApproval(data.id);
            setShowConfirmation(false);
          },
          dateTimeTitle: "",
          hasDateTime: false
        });
      } else {
        setConfirmationConfig({
          title: "Submit Transaction",
          message: "Are you sure you want to submit this transaction? Once submitted, it cannot be edited.",
          action: async () => {
            await handleSubmitForBypassApproval(data.id);
            setShowConfirmation(false);
          },
          dateTimeTitle: "",
          hasDateTime: false
        });
      }
    } else if (status === "Approved") { // Setting up to mark the employee as departed
      setConfirmationConfig({
        title: "Mark as Departed",
        message: "Are you sure you want to mark this employee as departed? This action will update the employee's status and may affect payroll.",
        action: async (values) => {
          await handleMarkAsDeparted(data.id);
          setShowConfirmation(false);
        },
        dateTimeTitle: "",
        hasDateTime: false
      });
    } else if (status === "Departed") { // Setting up to mark the employee as arrived
      setConfirmationConfig({
        title: "Mark as Onboard",
        message: "Are you sure you want to mark this employee as arrived? This action will update the employee's status and may affect payroll.",
        action: async (values) => {

          if(!values?.dateTime) {
             showGeneralMessage("Please select a valid date and time for arrival.", false);
             return;
          }
          
          await handleMarkAsArrived(data.id, values?.dateTime);
          setShowConfirmation(false);
        },
        dateTimeTitle: "Onboard Date: ",
        hasDateTime: true
      });
    }

    setShowConfirmation(true);

  };

  return (
    <div>
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
        <span className="text-lg font-semibold text-gray-600 block mb-4">Filters</span>
        <DynamicFilterBar
          requestedFilters={FEATURES.EMPLOYEE_FILTERS || []}
          activeFilterState={activeFilterState}
          onFilterChange={onFilterChange}
          showButtons={false}
        />
        <div className="flex gap-2 mt-5 border-t pt-4">
          <Button name="GENERATE" className="bg-blue-600 text-white px-5 py-2 border-none rounded cursor-pointer font-bold" onClick={applyFilters} />
          <Button name="CLEAR" className="bg-gray-400 text-white px-5 py-2 border-none rounded cursor-pointer" onClick={resetFilters} />
        </div>
      </div>

      <SearchBar handleFilter={handleFilter} />

      {userAccess?.isEmployeeTransactionProcessManager && (
        <Button
          icon={<AddIcon />}
          name="Create Transaction"
          className="bg-customGreen hover:bg-customGreenHover border-none cursor-pointer mb-2"
          onClick={() => openModal(null, "add")}
        />
      )}

      <CreateTable
        tableName="EmployeeTransactionsTable"
        headers={headers}
        accessors={accessors}
        data={data}
        actions={actions}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={10}
        totalItems={data?.length}
        customCellRenderer={renderCustomCell}
      />

      <EmployeeTransactionModal
        userAccess={userAccess}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        mode={mode}
        selectedData={selectedData}
        options={options}
        salaryData={salaryData}
        dropdownsLoading={dropdownsLoading}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        canEditAssignment={true}
        canEditCompensation={true}
        showGeneralMessage={showGeneralMessage}
      />

      <ConfirmationModal
        visible={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={(values) => {
          if (confirmationConfig.action) {
            confirmationConfig.action(values);
          }
        }}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        dateTimeTitle={confirmationConfig.dateTimeTitle}
        hasDateTime={confirmationConfig.hasDateTime}
        isLoading={confirmLoading}
      />

      <LoadingSpinner loading={loading || dropdownsLoading || requestLoading} />
    </div>
  );
};